/**
 * Curated model list for the Chat tab.
 *
 * The backend's GET /api/models returns exactly the models the signed-in
 * user may use (the Spark's Qwen for everyone, Scaleway models for
 * allowlisted users). All of them are served through the same /api/llm
 * proxy, which routes by the request's `model` field and re-enforces the
 * per-user gate server-side.
 */

import type { Model } from "@earendil-works/pi-ai";
import { sparkModel, SPARK_MODEL_ID, SPARK_PROVIDER } from "../spark-model.js";

interface ServerModel {
	id: string;
	name: string;
	contextWindow: number;
	/** Reasoning model with OpenAI-style reasoning_effort support. */
	reasoning?: boolean;
	/** The Spark's Qwen model — gets the qwen-specific thinking/template compat. */
	spark?: boolean;
	/** Whether the deployment accepts image input (e.g. false for llama.cpp without --mmproj). */
	images?: boolean;
}

let allowedModels: Model<"openai-completions">[] = [sparkModel()];

/** Models the current user may select. Synchronous accessor for the
 *  ModelSelector patch (see tools/model-menu.ts); populated by
 *  loadAllowedModels() during app boot. Falls back to just the Spark model
 *  until then. */
export function getAllowedModels(): Model<"openai-completions">[] {
	return allowedModels;
}

export async function loadAllowedModels(): Promise<Model<"openai-completions">[]> {
	try {
		const res = await fetch("/api/models");
		if (!res.ok) return allowedModels;
		const body = (await res.json()) as { models: ServerModel[] };
		allowedModels = body.models.map(buildModel);
		if (allowedModels.length === 0) allowedModels = [sparkModel()];
	} catch {
		/* keep fallback */
	}
	return allowedModels;
}

function buildModel(m: ServerModel): Model<"openai-completions"> {
	// The Spark model keeps its qwen-specific thinking/template config but
	// takes id/name/window/images from the server (env-driven — model swaps
	// on the Spark shouldn't require a frontend change).
	if (m.spark || m.id === SPARK_MODEL_ID) {
		return sparkModel({ id: m.id, name: m.name, contextWindow: m.contextWindow, images: m.images });
	}
	return {
		id: m.id,
		name: m.name,
		api: "openai-completions",
		// Same provider slug for all models so the seeded providerKeys entry
		// ("proxied-by-backend") satisfies pi-web-ui's key check.
		provider: SPARK_PROVIDER,
		baseUrl: `${window.location.origin}/api/llm`,
		reasoning: m.reasoning ?? false,
		// Reasoning models (GLM on Scaleway): map the UI thinking toggle onto
		// OpenAI-style reasoning_effort. The "off" default sends
		// reasoning_effort:"none" — without it GLM burns minutes of hidden
		// thinking on every prompt (the "super slow" report). Medium/High
		// opt back into deliberate reasoning (Scaleway supports none/high/max).
		...(m.reasoning
			? {
					thinkingLevelMap: {
						off: "none",
						minimal: "none",
						low: "none",
						medium: "high",
						high: "max",
					},
				}
			: {}),
		input: ["text", "image"],
		cost: { input: 0, output: 0, cacheRead: 0, cacheWrite: 0 },
		contextWindow: m.contextWindow,
		maxTokens: 8192,
		compat: {
			supportsDeveloperRole: false,
			supportsReasoningEffort: m.reasoning ?? false,
		},
	};
}
